/**
 * Transforms array-based entitlement API response into O(1) lookup maps.
 *
 * The API returns entitlements as arrays (D34):
 *   { gates: [{domain, target, effect, ...}], caps: [{domain, has, limit, used, ...}], ... }
 *
 * This transforms them into keyed maps for O(1) lookups:
 *   { gates: { "workflow_node:kling-video-lip-sync": {...} }, caps: { "project": {...} }, ... }
 *
 * Key format:
 * - Gates: `${domain}:${target}` (e.g., "workflow_node:kling-video-lip-sync")
 * - Caps/quotas/budgets/rateLimits: `${domain}` (e.g., "project", "workflow", "ai")
 */

import type {
  EntitlementResult,
  GateEntitlement,
  QuotaEntitlement,
  BudgetEntitlement,
  RateLimitEntitlement,
  ParameterGateEntry,
} from "@/types/entitlements";

interface RawGateEntry {
  domain: string;
  target: string;
  effect: "deny" | "allow";
  scope?: string;
  reasonTk?: string | null;
}

interface RawQuotaEntry {
  domain: string;
  has: boolean;
  limit: number;
  used: number;
  remaining: number;
  unitTk?: string;
  scope?: string;
  resetAt?: string;
}

interface RawBudgetEntry {
  domain: string;
  has: boolean;
  limitCents: number;
  usedCents: number;
  remainingCents: number;
  unitTk?: string;
  scope?: string;
  resetAt?: string;
}

interface RawRateLimitEntry {
  domain: string;
  has: boolean;
  limit: number;
  window: string;
  remaining: number;
}

interface RawEntitlementResponse {
  gates?: RawGateEntry[];
  caps?: RawQuotaEntry[];
  quotas?: RawQuotaEntry[];
  budgets?: RawBudgetEntry[];
  rateLimits?: RawRateLimitEntry[];
  parameterGates?: ParameterGateEntry[];
}

/**
 * Transforms array-based API entitlement response into keyed lookup maps.
 * Returns null if input is null/undefined.
 */
export function transformEntitlements(
  raw: RawEntitlementResponse | null | undefined,
): EntitlementResult | null {
  if (!raw) return null;

  const gates: Record<string, GateEntitlement> = {};
  const caps: Record<string, QuotaEntitlement> = {};
  const quotas: Record<string, QuotaEntitlement> = {};
  const budgets: Record<string, BudgetEntitlement> = {};
  const rateLimits: Record<string, RateLimitEntitlement> = {};

  // Gates: key by domain:target
  if (Array.isArray(raw.gates)) {
    for (const g of raw.gates) {
      const key = `${g.domain}:${g.target}`;
      gates[key] = {
        effect: g.effect,
        scope: g.scope ?? "",
        reasonTk: g.reasonTk,
      };
    }
  }

  // Caps: key by domain (last-wins if duplicates — API should guarantee unique domains)
  if (Array.isArray(raw.caps)) {
    for (const c of raw.caps) {
      caps[c.domain] = {
        has: c.has,
        limit: c.limit,
        used: c.used,
        remaining: c.remaining,
        unitTk: c.unitTk,
        scope: c.scope,
        resetAt: c.resetAt,
      };
    }
  }

  // Quotas: key by domain
  if (Array.isArray(raw.quotas)) {
    for (const q of raw.quotas) {
      quotas[q.domain] = {
        has: q.has,
        limit: q.limit,
        used: q.used,
        remaining: q.remaining,
        unitTk: q.unitTk,
        scope: q.scope,
        resetAt: q.resetAt,
      };
    }
  }

  // Budgets: key by domain
  if (Array.isArray(raw.budgets)) {
    for (const b of raw.budgets) {
      budgets[b.domain] = {
        has: b.has,
        limitCents: b.limitCents,
        usedCents: b.usedCents,
        remainingCents: b.remainingCents,
        unitTk: b.unitTk,
        scope: b.scope,
        resetAt: b.resetAt,
      };
    }
  }

  // Rate limits: key by domain
  if (Array.isArray(raw.rateLimits)) {
    for (const r of raw.rateLimits) {
      rateLimits[r.domain] = {
        has: r.has,
        limit: r.limit,
        window: r.window,
        remaining: r.remaining,
      };
    }
  }

  // Parameter gates: pass through as array (frontend builds lookups per-node via useParameterGates)
  const parameterGates: ParameterGateEntry[] = Array.isArray(raw.parameterGates)
    ? raw.parameterGates
    : [];

  // Return null if everything is empty (no policies exist)
  const hasAny =
    Object.keys(gates).length > 0 ||
    Object.keys(caps).length > 0 ||
    Object.keys(quotas).length > 0 ||
    Object.keys(budgets).length > 0 ||
    Object.keys(rateLimits).length > 0 ||
    parameterGates.length > 0;

  if (!hasAny) return null;

  return { gates, quotas, rateLimits, budgets, caps, parameterGates };
}
